/**
 * @fileoverview Global menu / permission definitions.
 *
 * ─── GLOSSARY: Warehouse master data ────────────────────────────────────────
 * ชื่อที่ผู้ใช้เห็นบนหน้าจอถูกเปลี่ยนแล้ว แต่ชื่อ table / field / API / permission
 * ยังคงเดิม (เพื่อไม่ให้กระทบ external API, role permission ที่เก็บใน DB และ migration)
 *
 *   UI (ใหม่)       | ไทย         | Table    | API route          | permission | field ใน part_masters
 *   ----------------|-------------|----------|--------------------|------------|----------------------
 *   Material Type   | ประเภทวัสดุ  | brands   | warehouse/brands   | brands.*   | brand_id / brand_name
 *   Material Group  | กลุ่มวัสดุ    | models   | warehouse/models   | models.*   | model_id / model_name
 *   Brand           | แบรนด์       | series   | warehouse/series   | serie.*    | serie_id / serie_name
 *
 * ลำดับชั้น: Material Type (2 ระดับ) -> Material Group ; Brand เป็น master แยกอิสระ
 * i18n key ที่ใช้: material_type* / material_group* / brand*
 * ─────────────────────────────────────────────────────────────────────────────
 */

global.version = "1.0.3";

/**
 * เช็คว่า user มี permission ให้เข้าโมดูลนี้ได้ไหม โดยดูจากทุกเมนูในโมดูล
 * (ไม่ใช่แค่ permission ของหน้า dashboard) ป้องกันกรณี user มีสิทธิ์เมนูอื่น
 * ในโมดูล แต่ไม่มีสิทธิ์ dashboard แล้วเข้าโมดูลไม่ได้เลย
 * @param {Array} menu - global.menu_admin / menu_customer / menu_warehouse / menu_super_user
 * @param {Array<string>} perms - user_profile.role.permission
 * @returns {boolean}
 */
// เมนูที่คุมด้วย "ประเภทผู้ใช้" แทน permission (item.userTypes) - ตอนนี้มีแค่ "ตั้งค่าระบบ" = super_user / it_admin
// (ตกลง 2026-10-13) ไม่เช็ค permission ของ role เลย, ไม่โชว์ในหน้าบทบาท, backend ล็อกการเขียนด้วย checkSystemSettingAccess ชุดเดียวกัน
global.SYSTEM_SETTINGS_USER_TYPES = ["super_user", "it_admin"];

// รายการที่มี userTypes = คุมด้วยประเภทผู้ใช้ล้วน (true/false ตามประเภท) / ไม่มี = ไม่เกี่ยว คืน null ให้ไปเช็ค permission ตามปกติ
global.menuItemTypeAccess = function (item, profile) {
  if (!item?.userTypes) return null;
  return item.userTypes.includes(profile?.user_type);
};

// ใช้เมนูรายการนี้ได้ไหม - เมนูที่คุมด้วยประเภทดูแค่ประเภท (ไม่ส่ง profile = ไม่ผ่าน) เมนูอื่นดู permission
global.canAccessMenuItem = function (item, perms, profile = null) {
  const byType = global.menuItemTypeAccess(item, profile);
  if (byType !== null) return byType;
  // permissionAny = สิทธิ์ทางเลือกที่เข้าเมนู/หน้านี้ได้เหมือน permission (เช่น Activity โครงการ: .create อย่างเดียวก็เข้าได้)
  return [item.permission, ...(item.permissionAny || [])].some((key) => !!key && (perms || []).includes(key));
};

global.hasModuleAccess = function (menu, perms, profile = null) {
  return (menu || []).some((item) => item.link && !item.hidden && global.canAccessMenuItem(item, perms, profile));
};

/**
 * หาลิงก์แรกในเมนูที่ user มี permission เข้าถึง ใช้แทนการ hardcode ไปหน้า dashboard
 * @param {Array} menu - global.menu_admin / menu_customer / menu_warehouse / menu_super_user
 * @param {Array<string>} perms - user_profile.role.permission
 * @returns {string|null}
 */
global.getFirstAccessibleLink = function (menu, perms, profile = null) {
  const item = (menu || []).find((m) => m.link && !m.hidden && global.canAccessMenuItem(m, perms, profile));
  return item ? item.link : null;
};

// ซ่อนเมนูหน้าแจ้งเตือน ประกันใกล้หมด / ไส้กรอง / แผน PM / ตั้งค่าแจ้งเตือน (ผู้ใช้สั่ง "ซ่อนเมนูไว้ก่อน" 2026-10-23, เปิดกลับ 2026-10-24)
// = ไม่แสดงในเมนูซ้าย ไม่แสดง badge ตัวเลขแดงและกระดิ่งแจ้งเตือน แต่คง route + permission + แถวในหน้า Roles ไว้ครบ
// (entry ยังอยู่ใน menu_admin เพราะ PermissionRoute อ่าน map link -> permission จากที่นี่ ถ้าลบ path จะกลายเป็นเข้าได้ทุกคน)
// เปิดกลับ: ตั้งเป็น false
// entry ที่มี hidden: true = ไม่โชว์ในเมนู (Navbar ข้าม) แต่ยังประกาศ link -> permission ให้ PermissionRoute ตรวจสิทธิ์
global.ALERT_PAGES_MENU_HIDDEN = false;

global.menu_admin = [
    { title: "main_menu" },
    { icon: "\uf0e4", name: "dashboard", link: "/ad/dashboard", permission: "dashboard-admin.read", component: "" },
    { icon: "\uf01c", name: "work_requests", link: "/ad/work-requests", permission: "wr.read", component: "" },
    { icon: "\uf46d", name: "work_orders", link: "/ad/work-orders", permission: "wo.read", component: "" },
    // ใบขอเบิกอะไหล่ใน WO Normal Sales - ไม่มีหน้าของตัวเอง ต้องมี entry link:"" เพื่อให้หน้า Roles ติ๊กได้
    { icon: "\uf466", name: "pl_menu_picking_list", link: "", permission: "wo_picking_list.read", component: "" },
    // เอกสารเบิกอะไหล่ WBS (WO โครงการ BU05) - ไม่มีหน้าของตัวเอง ต้องมี entry link:"" เพื่อให้หน้า Roles ติ๊กได้ (ส่ง SAP/ยกเลิก = สิทธิ์เดี่ยว)
    { icon: "\uf468", name: "wbi_menu_issue", link: "", permission: "wo_wbs_issue.read", component: "" },
    { icon: "\uf1d8", name: "wbi_menu_issue_send", link: "", permission: "wo_wbs_issue.send" },
    { icon: "\uf05e", name: "wbi_menu_issue_cancel", link: "", permission: "wo_wbs_issue.cancel" },
    { icon: "\uf073", name: "scheduling", link: "/ad/scheduling", permission: "scheduling.read", component: "" },
    { icon: "\uf073", name: "sales_visit_planning", link: "/ad/sales-visit-planning", permission: "sale-planning.read", component: "" },
    { title: "preventive_maintenance" },
    { icon: "\uf0ae", name: "contract_plan", link: "/ad/contract-plan", permission: "contract-plan.read", component: "" },
    { icon: "\uf46c", name: "inspection_plan", link: "/ad/inspection-plan", permission: "inspection-plan.read", component: "" },
    { icon: "\uf201", name: "pm_dashboard", link: "/ad/pm-dashboard", permission: "pm.read", component: "" },
    { icon: "\uf03a", name: "checklist_templates", link: "/ad/checklist-templates", permission: "checklist-templates.read", component: "" },
    { title: "reports_analytics" },
    { icon: "\uf15c", name: "work_order_summary", link: "/ad/work-order-summary", permission: "wo.read", component: "" },
    { icon: "\uf058", name: "pm_compliance", link: "/ad/pm-compliance", permission: "pm.read", component: "" },
    { icon: "\uf200", name: "asset_report", link: "/ad/asset-report", permission: "asset.read", component: "" },
    // หน้าย่อยของประวัติทะเบียนสินทรัพย์ (ค้นหา/เรียกดู + Asset 360) - ไม่โชว์ในเมนู แต่ต้องประกาศ link -> asset.read ไม่งั้น PermissionRoute ปล่อยผ่านทุกคน
    // ใช้ name เดียวกับเมนูหลัก เพื่อไม่เพิ่มชื่อโมดูลซ้ำในแถวสิทธิ์หน้า Roles
    { icon: "\uf200", name: "asset_report", link: "/ad/asset-history-list", permission: "asset.read", hidden: true, component: "" },
    { icon: "\uf200", name: "asset_report", link: "/ad/asset-history-detail", permission: "asset.read", hidden: true, component: "" },
    { icon: "\uf15c", name: "pool_care_monthly_report", link: "/ad/pool-care-monthly-report", permission: "pool_care_report.read", actions: ["read"], component: "" },
    // สิทธิ์ปุ่ม Print ทั้งหมด - ไม่มีหน้าของตัวเอง ต้องมี entry link:"" เพื่อให้หน้า Roles ติ๊กได้
    { icon: "\uf02f", name: "pool_care_report_export", link: "", permission: "pool_care_report.export" },
    { icon: "\uf0c0", name: "technician_reports", link: "/ad/technician-reports", permission: "technician-report.read", actions: ["read"], component: "" },
    { icon: "\uf3c5", name: "mileage_report", link: "/ad/mileage", permission: "mileage.read", actions: ["read"], component: "" },
    // สิทธิ์ปุ่ม Export - ไม่มีหน้าของตัวเอง ต้องมี entry link:"" เพื่อให้หน้า Roles ติ๊กได้
    { icon: "", name: "mileage_report_export", link: "", permission: "mileage.export" },
    // รายงานสรุปงานเสร็จ/การชำระเงิน Normal Sales (กรอง BU จากหมวดคำร้อง)
    { icon: "\uf155", name: "nsp_menu_report", link: "/ad/normal-sales-payment-report", permission: "normal_sales_payment_report.read", actions: ["read"], component: "" },
    { icon: "\uf56d", name: "nsp_menu_report_export", link: "", permission: "normal_sales_payment_report.export" },
    // รายงานใกล้หมดประกัน (Asset + สัญญาดูแลสระ) - export/followup ไม่มีหน้าของตัวเอง ต้องมี entry link:"" เพื่อให้หน้า Roles ติ๊กได้
    { icon: "\uf3ed", name: "wx_menu_report", link: "/ad/warranty-expiry", permission: "warranty_expiry.read", actions: ["read"], hidden: global.ALERT_PAGES_MENU_HIDDEN, component: "" },
    { icon: "\uf56d", name: "wx_menu_report_export", link: "", permission: "warranty_expiry.export" },
    { icon: "\uf044", name: "wx_menu_report_followup", link: "", permission: "warranty_expiry.followup" },
    // รายงานกำหนดเปลี่ยนไส้กรอง (ทุกแผนก) - export/followup ไม่มีหน้าของตัวเอง ต้องมี entry link:"" เพื่อให้หน้า Roles ติ๊กได้
    { icon: "\uf0b0", name: "fa_menu_report", link: "/ad/filter-alert", permission: "filter_alert.read", actions: ["read"], hidden: global.ALERT_PAGES_MENU_HIDDEN, component: "" },
    { icon: "\uf56d", name: "fa_menu_report_export", link: "", permission: "filter_alert.export" },
    { icon: "\uf044", name: "fa_menu_report_followup", link: "", permission: "filter_alert.followup" },
    // แจ้งเตือนแผน PM (ใกล้ถึงกำหนด / เกินกำหนด) - export/postpone/create_wo ไม่มีหน้าของตัวเอง ต้องมี entry link:"" เพื่อให้หน้า Roles ติ๊กได้
    { icon: "\uf0ad", name: "pa_menu_report", link: "/ad/pm-alert", permission: "pm_alert.read", actions: ["read"], hidden: global.ALERT_PAGES_MENU_HIDDEN, component: "" },
    { icon: "\uf56d", name: "pa_menu_report_export", link: "", permission: "pm_alert.export" },
    { icon: "\uf073", name: "pa_menu_report_postpone", link: "", permission: "pm_alert.postpone" },
    { icon: "\uf0ae", name: "pa_menu_report_create_wo", link: "", permission: "pm_alert.create_wo" },
    // { icon: "\uf571", name: "cost_report", link: "/ad/cost-report", permission: "dashboard-admin.read", component: "" },
    { title: "master_data" },
    { icon: "\uf007", name: "customers", link: "/ad/customers", permission: "customers.read", component: "" },
    { icon: "\uf007", name: "sale", link: "/ad/sale", permission: "sale-master.read", component: "" },
    // ปิดเมนู finance ไว้ก่อน (ยังไม่ได้ใช้งาน) - permission "finance-master.*" ยังคงอยู่ใน global.permission
    // และยังถูกส่งกลับไปกับ payload ตอนบันทึก role (ผ่าน uncontrolledPermissions ใน roles.js) ตามเดิม
    // { icon: "\uf007", name: "finance", link: "/ad/finance", permission: "finance-master.read", component: "" },
    { icon: "\uf1b3", name: "assets", link: "/ad/assets", permission: "asset.read", component: "" },
    { icon: "\uf0ad", name: "technicians", link: "/ad/technicians", permission: "technicians.read", component: "" },
    { icon: "\uf140", name: "objectives", link: "/ad/objectives", permission: "objective.read", component: "" },
    { icon: "\uf132", name: "customer_warranty_conditions", link: "/ad/customer-warranty-conditions", permission: "customer-warranty-condition.read", component: "" },
    { icon: "\uf0e8", name: "projects", link: "/ad/projects", permission: "projects.read", component: "" },
    { icon: "\uf1b9", name: "company_vehicles", link: "/ad/company-vehicles", permission: "company-vehicle.read", component: "" },
    // ปฏิทินงานช่าง (มือถือ) - วันหยุดบริษัท + วันลาส่วนตัวของช่าง (ISS)
    { icon: "\uf133", name: "company_holidays", link: "/ad/company-holidays", permission: "company-holiday.read", component: "" },
    { icon: "\uf073", name: "technician_leaves", link: "/ad/technician-leaves", permission: "technician-leave.read", component: "" },
    // กลุ่ม "บันทึกเวลา" - สิทธิ์ที่ไม่ใช่ .read ไม่มีหน้าของตัวเอง ต้องมี entry link:"" เพื่อให้หน้า Roles ติ๊กได้
    { title: "att_group_title" },
    { icon: "\uf017", name: "att_menu_attendance", link: "/ad/attendance", permission: "attendance.read", actions: ["read"], component: "" },
    { icon: "\uf56d", name: "att_menu_attendance_export", link: "", permission: "attendance.export" },
    { icon: "\uf044", name: "att_menu_attendance_override", link: "", permission: "attendance.override", actions: ["read"] },
    { icon: "\uf058", name: "att_menu_attendance_approve", link: "", permission: "attendance.approve" },
    { icon: "\uf013", name: "att_menu_attendance_setting", link: "", permission: "attendance.setting" },
    { icon: "\uf0ae", name: "att_menu_project_activities", link: "/ad/project-activities", permission: "project_activity.read", permissionAny: ["project_activity.create"], actions: ["read"], component: "" },
    { icon: "\uf56d", name: "att_menu_project_activity_export", link: "", permission: "project_activity.export" },
    // สิทธิ์เดี่ยว: พนักงานสำนักงานบันทึก Activity ของตัวเองบนเว็บ (ผู้มีแค่ .create เข้าหน้า /ad/project-activities ได้ เห็นแค่ "Activity ของฉัน")
    { icon: "\uf067", name: "att_menu_project_activity_create", link: "", permission: "project_activity.create", actions: ["read"] },
    // รายงานลงเวลาทำงาน (Timesheet) - รวมเวลาจาก แจ้งซ่อม/PM + ติดตั้ง + ดูแลสระ + Activity ต่อคน/วัน
    { icon: "\uf1da", name: "ts_menu_timesheet_report", link: "/ad/timesheet-report", permission: "timesheet_report.read", actions: ["read"], component: "" },
    { icon: "\uf56d", name: "ts_menu_timesheet_report_export", link: "", permission: "timesheet_report.export" },
    { title: "user_management" },
    { icon: "\uf0c0", name: "users", link: "/ad/users", permission: "users.read", component: "" },
    { icon: "\uf084", name: "roles", link: "/ad/roles", permission: "roles.read", component: "" },
    { icon: "\uf508", name: "department", link: "/ad/departments", permission: "departments.read", component: "" },
    { title: "settings" },
    // ตั้งค่าการแจ้งเตือนก่อนหมดประกัน (ต่อแผนก) - ซ่อนจากผู้ไม่มีสิทธิ์ warranty_alert.setting
    { icon: "\uf0f3", name: "wx_menu_alert_settings", link: "/ad/warranty-alert-settings", permission: "warranty_alert.setting", actions: ["read"], hidden: global.ALERT_PAGES_MENU_HIDDEN, component: "" },
    // "ตั้งค่าระบบ" + แท็บย่อยทั้งหมด คุมด้วยประเภทผู้ใช้ (userTypes) ล้วน: เห็น/เข้าได้เฉพาะ super_user กับ IT Admin
    // ไม่เช็ค permission และไม่แสดงในหน้าบทบาท (roles.js ข้ามรายการที่มี userTypes) - permission key คงไว้เพื่ออ้างอิงเท่านั้น
    { icon: "\uf013", name: "workrequest-config-wireframe", link: "/ad/workrequest-config-wireframe", permission: "wr-category.read", component: "", userTypes: global.SYSTEM_SETTINGS_USER_TYPES },
    { icon: "\uf013", name: "req_category", link: "", permission: "wr-category.read", userTypes: global.SYSTEM_SETTINGS_USER_TYPES },
    { icon: "\uf013", name: "service_type", link: "", permission: "wr-service-type.read", userTypes: global.SYSTEM_SETTINGS_USER_TYPES },
    { icon: "\uf013", name: "priority_level", link: "", permission: "wr-priority.read", userTypes: global.SYSTEM_SETTINGS_USER_TYPES },
    { icon: "\uf013", name: "reject_reason", link: "", permission: "wr-reject-reason.read", userTypes: global.SYSTEM_SETTINGS_USER_TYPES },
    { icon: "", name: "ta_tab", link: "", permission: "wr-travel-allowance.read", userTypes: global.SYSTEM_SETTINGS_USER_TYPES },
    { icon: "", name: "rn_tab", link: "", permission: "wr-running-number.read", userTypes: global.SYSTEM_SETTINGS_USER_TYPES },
    { icon: "\uf0e0", name: "email_tab", link: "", permission: "email-recipient.read", userTypes: global.SYSTEM_SETTINGS_USER_TYPES },
    { icon: "\uf013", name: "service-bom-config", link: "/ad/service-bom-config", permission: "bom-master.read", component: "" },
    // master สาเหตุหลัก (ISS-029) - ช่างเลือกตอนปิดงานในแอป
    { icon: "\uf0ae", name: "root_causes", link: "/ad/root-causes", permission: "root-cause.read", component: "" },
    // ต้นแบบบันทึกเวลา: "ประเภท Activity" อยู่ใต้กลุ่ม "ตั้งค่า"
    { icon: "\uf022", name: "att_menu_activity_types", link: "", permission: "activity_type.read", component: "", userTypes: global.SYSTEM_SETTINGS_USER_TYPES },
    { icon: "\uf1da", name: "audit_logs", link: "/ad/audit-logs", permission: "audit-logs.read", component: "" },
    // { icon: "\uf013", name: "setting", link: "/ad/settings", permission: "settings.read" },
];

// สิทธิ์ของแอปช่าง (มือถือ) - ไม่มีหน้าเว็บ ใช้แสดงในแท็บ "แอปมือถือ" ของหน้า Roles เท่านั้น
// (ไม่ถูกนำไปสร้างเมนู/PermissionRoute ของเว็บ)
global.menu_mobile = [
    // ปุ่ม ➕ สร้างแผนงานติดตั้งบนแอป (ผู้ใช้ตัดสิน 2026-10-12 คุมด้วย Role แทนกฎแผนก)
    { icon: "\uf073", name: "contract_install_plan_create", link: "", permission: "contract-plan-install.create" },
    // ปุ่ม ✅ เช็คอินโดยพลการของงานติดตั้งบนแอป (การประชุมลูกค้า 2026-10-02)
    { icon: "\uf3c5", name: "contract_free_checkin", link: "", permission: "contract-checkin.create" },
];

global.menu_customer = [
    { title: "overview" },
    { icon: "\uf0e4", name: "dashboard", link: "/cs/dashboard", permission: "dashboard-customer.read", component: "🟡" },
    { icon: "\uf1b2", name: "my_assets", link: "/cs/assets", permission: "asset.read" },
    { title: "work_management" },
    { icon: "\uf0ae", name: "work_requests", link: "/cs/work-requests", permission: "wo.read" },
    { icon: "\uf073", name: "appointments", link: "/cs/appointments", permission: "wo.read", component: "🟡" },
    { title: "asset_service_info" },
    { icon: "\uf1da", name: "service_history", link: "/cs/service-history", permission: "wo.read", component: "🟡" },
    { icon: "\uf15c", name: "service_summary", link: "/cs/service-summary", permission: "wo.read", component: "🟡" },
    { title: "report" },
    { icon: "\uf466", name: "asset_report", link: "/cs/asset-report", permission: "wo.read", component: "" },
    // หน้ารายละเอียดสินทรัพย์ฝั่งลูกค้า - ไม่โชว์ในเมนู แต่ประกาศ link -> wo.read ให้ PermissionRoute ตรวจสิทธิ์ (name เดียวกับเมนูหลัก ไม่เพิ่มแถวในหน้า Roles)
    { icon: "\uf466", name: "asset_report", link: "/cs/asset-history-detail", permission: "wo.read", hidden: true, component: "" },
    { icon: "\uf155", name: "cost_report", link: "/cs/cost-report", permission: "wo.read", component: "🟡" },
    { icon: "\uf201", name: "sla_report", link: "/cs/sla-report", permission: "wo.read", component: "🟡" },
];

global.menu_warehouse = [
    { title: "overview" },
    { icon: "\uf0e4", name: "dashboard", link: "/wh/dashboard", permission: "dashboard-warehouse.read", component: "" },
    // ปิดเมนู Asset (บันทึกรับประกัน Vendor) ไว้ก่อน ตามที่ตกลงไว้
    // เปิดกลับ: uncomment 2 บรรทัดนี้ + uncomment route /wh/assets ใน routes.js
    // (ต้อง uncomment คู่กันเสมอ เพราะ PermissionRoute อ่าน map สิทธิ์จากไฟล์นี้
    //  ถ้าเหลือแต่ route แต่ไม่มีเมนู path จะกลายเป็น "ไม่ประกาศสิทธิ์" = เข้าได้ทุกคน)
    // { title: "asset_management" },
    // { icon: "\uf1b3", name: "assets", link: "/wh/assets", permission: "warehouse-asset.read", component: "" },
    { title: "inventory_management" },
    { icon: "\uf468", name: "stock", link: "/wh/stock", permission: "stock.read", component: "" },
    // { icon: "\uf0ad", name: "mechanic_inventory", link: "/wh/technician-stock", permission: "stock-technician.read", component: "🟡" },
    { icon: "\uf02d", name: "parts_information", link: "/wh/part-master", permission: "part-master.read", component: "" },
    { title: "parts_movements" },
    { icon: "\uf56d", name: "goods_issue", link: "/wh/goods-issue", permission: "stock-withdraw.read", component: "" },
    // { icon: "\uf01e", name: "parts_returning", link: "/wh/parts-return", permission: "stock-return.read", component: "🟡" },
    // { icon: "\uf24e", name: "stock_counting", link: "/wh/stock-audit", permission: "stock-audit.read", component: "🟡" },
    { icon: "\uf24e", name: "adjustment_stocks", link: "/wh/adjustment-stocks", permission: "stock-adjustment.read", component: "" },
    { title: "procurement" },
    // PR และ PO แยกเมนูกัน แต่ใช้ component เดียวกัน (pr-po.js) โดยส่ง prop tab จาก routes.js
    { icon: "\uf15b", name: "pr_tab", link: "/wh/pr", permission: "pr.read", component: "" },
    { icon: "\uf571", name: "po_tab", link: "/wh/po", permission: "po.read", component: "" },
    { icon: "\uf46c", name: "goods_receiving", link: "/wh/goods-receipt", permission: "gr.read", component: "" },
    { title: "supplier_management" },
    // { icon: "\uf3ed", name: "supplier_claims", link: "/wh/stock-claim", permission: "stock-claim.read", component: "🟡" },
    { icon: "\uf0c0", name: "suppliers", link: "/wh/supplier", permission: "supplier.read", component: "" },
    { title: "master_data" },
    { icon: "\uf494", name: "warehouse", link: "/wh/warehouse", permission: "warehouse.read", component: "" },
    { icon: "\uf3c5", name: "location", link: "/wh/location", permission: "location.read", component: "" },
    { icon: "\uf546", name: "uoms", link: "/wh/uoms", permission: "uoms.read", component: "" },
    { icon: "\uf5fd", name: "type", link: "/wh/part-categories", permission: "part-categories.read", component: "" },
    // --- Warehouse master data: ชื่อที่ผู้ใช้เห็น != ชื่อ table/permission (ดู glossary หัวไฟล์) ---
    // Material Type  = table `brands`  (2 ระดับ: parent/child) -> part_masters.brand_id
    { icon: "\uf02c", name: "material_type", link: "/wh/brands", permission: "brands.read", component: "" },
    // Material Group = table `models`  (belongsTo brands)      -> part_masters.model_id
    { icon: "\uf1b2", name: "material_group", link: "/wh/models", permission: "models.read", component: "" },
    // Brand          = table `series`                          -> part_masters.serie_id
    { icon: "\uf233", name: "brand", link: "/wh/series", permission: "serie.read", component: "" },
    { icon: "\uf27a", name: "reasons", link: "/wh/reasons", permission: "reason.read", component: "" },
    { icon: "\uf1ad", name: "branches", link: "/wh/branches", permission: "branch.read", component: "" },
    { title: "data_tools" },
    { icon: "", name: "import_center", link: "/wh/import-center", permission: "import-center.read", component: "" },
    // { title: "settings" },
    // { icon: "\uf013", name: "setting", link: "/wh/settings", permission: "settings.read", component: "" },
];

global.menu_super_user = [
  { title: "main" },
  { icon: "\uf0ac", name: "company_management", link: "/su/companies", permission: "companies.read", component: "" },
];

global.permission = [
    // ADMIN
    "dashboard-admin.read",
    "dashboard-admin.create",
    "dashboard-admin.update",
    "dashboard-admin.delete",

    "asset.read",
    "asset.create",
    "asset.update",
    "asset.delete",

    "assets-report.read",
    "assets-report.create",
    "assets-report.update",
    "assets-report.delete",

    "audit-logs.read",
    "audit-logs.create",
    "audit-logs.update",
    "audit-logs.delete",

    "bom-master.read",
    "bom-master.create",
    "bom-master.update",
    "bom-master.delete",

    "checklist-templates.read",
    "checklist-templates.create",
    "checklist-templates.update",
    "checklist-templates.delete",

    "companies.read",
    "companies.create",
    "companies.update",
    "companies.delete",

    "cost-report.read",
    "cost-report.create",
    "cost-report.update",
    "cost-report.delete",

    "customers.read",
    "customers.create",
    "customers.update",
    "customers.delete",

    "dashboard.read",
    "dashboard.create",
    "dashboard.update",
    "dashboard.delete",

    "departments.read",
    "departments.create",
    "departments.update",
    "departments.delete",

    "finance-master.read",
    "finance-master.create",
    "finance-master.update",
    "finance-master.delete",

    "contract-plan.read",
    "contract-plan.create",
    "contract-plan.update",
    "contract-plan.delete",

    "inspection-plan.read",
    "inspection-plan.create",
    "inspection-plan.update",
    "inspection-plan.delete",

    "part-controls.read",
    "part-controls.create",
    "part-controls.update",
    "part-controls.delete",

    "permissions.read",
    "permissions.create",
    "permissions.update",
    "permissions.delete",

    "pm.read",
    "pm.create",
    "pm.update",
    "pm.delete",

    "pm-report.read",
    "pm-report.create",
    "pm-report.update",
    "pm-report.delete",

    "roles.read",
    "roles.create",
    "roles.update",
    "roles.delete",

    "sale-master.read",
    "sale-master.create",
    "sale-master.update",
    "sale-master.delete",

    "objective.read",
    "objective.create",
    "objective.update",
    "objective.delete",

    "customer-warranty-condition.read",
    "customer-warranty-condition.create",
    "customer-warranty-condition.update",
    "customer-warranty-condition.delete",

    "sale-planning.read",
    "sale-planning.create",
    "sale-planning.update",
    "sale-planning.delete",

    "scheduling.read",
    "scheduling.create",
    "scheduling.update",
    "scheduling.delete",

    "service-bom.read",
    "service-bom.create",
    "service-bom.update",
    "service-bom.delete",

    "technicians.read",
    "technicians.create",
    "technicians.update",
    "technicians.delete",

    "users.read",
    "users.create",
    "users.update",
    "users.delete",

    "wo.read",
    "wo.create",
    "wo.update",
    "wo.delete",

    "wo-report.read",
    "wo-report.create",
    "wo-report.update",
    "wo-report.delete",

    "wr.read",
    "wr.create",
    "wr.update",
    "wr.delete",

    "wr-category.read",
    "wr-category.create",
    "wr-category.update",
    "wr-category.delete",

    "wr-reject-reason.read",
    "wr-reject-reason.create",
    "wr-reject-reason.update",
    "wr-reject-reason.delete",

    "wr-priority.read",
    "wr-priority.create",
    "wr-priority.update",
    "wr-priority.delete",

    "wr-service-type.read",
    "wr-service-type.create",
    "wr-service-type.update",
    "wr-service-type.delete",

    "wr-travel-allowance.read",
    "wr-travel-allowance.create",
    "wr-travel-allowance.update",
    "wr-travel-allowance.delete",

    "wr-running-number.read",
    "wr-running-number.create",
    "wr-running-number.update",
    "wr-running-number.delete",

    "email-recipient.read",
    "email-recipient.create",
    "email-recipient.update",
    "email-recipient.delete",

    "dashboard-customer.read",
    "dashboard-customer.create",
    "dashboard-customer.update",
    "dashboard-customer.delete",

    // WAREHOUSE
    "dashboard-warehouse.read",
    "dashboard-warehouse.create",
    "dashboard-warehouse.update",
    "dashboard-warehouse.delete",

    "brands.read",
    "brands.create",
    "brands.update",
    "brands.delete",

    "gr.read",
    "gr.create",
    "gr.update",
    "gr.delete",

    "location.read",
    "location.create",
    "location.update",
    "location.delete",

    "models.read",
    "models.create",
    "models.update",
    "models.delete",

    "part-categories.read",
    "part-categories.create",
    "part-categories.update",
    "part-categories.delete",

    "part-master.read",
    "part-master.create",
    "part-master.update",
    "part-master.delete",

    "wo_picking_list.read",
    "wo_picking_list.create",
    "wo_picking_list.update",
    "wo_picking_list.delete",

    "wo_wbs_issue.read",
    "wo_wbs_issue.create",
    "wo_wbs_issue.update",
    "wo_wbs_issue.delete",
    "wo_wbs_issue.send",
    "wo_wbs_issue.cancel",

    "po.read",
    "po.create",
    "po.update",
    "po.delete",

    "pr.read",
    "pr.create",
    "pr.update",
    "pr.delete",

    "reason.read",
    "reason.create",
    "reason.update",
    "reason.delete",

    "branch.read",
    "branch.create",
    "branch.update",
    "branch.delete",

    "serie.read",
    "serie.create",
    "serie.update",
    "serie.delete",

    "stock.read",
    "stock.create",
    "stock.update",
    "stock.delete",

    "stock-audit.read",
    "stock-audit.create",
    "stock-audit.update",
    "stock-audit.delete",

    "stock-adjustment.read",
    "stock-adjustment.create",
    "stock-adjustment.update",
    "stock-adjustment.delete",

    "stock-claim.read",
    "stock-claim.create",
    "stock-claim.update",
    "stock-claim.delete",

    "stock-return.read",
    "stock-return.create",
    "stock-return.update",
    "stock-return.delete",

    "stock-technician.read",
    "stock-technician.create",
    "stock-technician.update",
    "stock-technician.delete",

    "stock-withdraw.read",
    "stock-withdraw.create",
    "stock-withdraw.update",
    "stock-withdraw.delete",

    "supplier.read",
    "supplier.create",
    "supplier.update",
    "supplier.delete",

    "uoms.read",
    "uoms.create",
    "uoms.update",
    "uoms.delete",

    "warehouse.read",
    "warehouse.create",
    "warehouse.update",
    "warehouse.delete",

    "import-center.read",
    "import-center.create",
    "import-center.update",
    "import-center.delete",

    "warehouse-asset.read",
    "warehouse-asset.create",
    "warehouse-asset.update",
    "warehouse-asset.delete",

    "projects.read",
    "projects.create",
    "projects.update",
    "projects.delete",

    "company-vehicle.read",
    "company-vehicle.create",
    "company-vehicle.update",
    "company-vehicle.delete",

    "root-cause.read",
    "root-cause.create",
    "root-cause.update",
    "root-cause.delete",

    "company-holiday.read",
    "company-holiday.create",
    "company-holiday.update",
    "company-holiday.delete",

    "technician-leave.read",
    "technician-leave.create",
    "technician-leave.update",
    "technician-leave.delete",

    "mileage.read",
    "mileage.export",

    "attendance.read",
    "attendance.export",
    "attendance.override",
    "attendance.approve",
    "attendance.setting",

    "project_activity.read",
    "project_activity.export",
    "project_activity.create",

    "timesheet_report.read",
    "timesheet_report.export",

    "warranty_expiry.read",
    "warranty_expiry.export",
    "warranty_expiry.followup",
    "filter_alert.read",
    "filter_alert.export",
    "filter_alert.followup",
    "pm_alert.read",
    "pm_alert.export",
    "pm_alert.postpone",
    "pm_alert.create_wo",
    "warranty_alert.setting",

    "normal_sales_payment_report.read",
    "normal_sales_payment_report.export",

    "activity_type.read",
    "activity_type.create",
    "activity_type.update",
    "activity_type.delete",

    "pool_care_report.read",
    "pool_care_report.export",

    "technician-report.read",

    "contract-checkin.create",
    "contract-plan-install.create",
];

global.bank = [
  { icon: "CIMB.png", value: "CIMB", label: "CIMB Thai", name_th: "ธนาคารซีไอเอ็มบีไทย", code: "022",},
  { icon: "LHB.png", value: "LHB", label: "Land and Houses Bank", name_th: "ธนาคารแลนด์แอนด์เฮ้าส์", code: "073",},
  { icon: "TCRB.png", value: "TCRB", label: "The Thai Credit Retail Bank", name_th: "ธนาคารไทยเครดิตเพื่อรายย่อย", code: "071",},
  { icon: "KKP.png", value: "KKP", label: "Kiatnakin Bank", name_th: "ธนาคารเกียรตินาคิน", code: "069",},
  { icon: "TISCO.png", value: "TISCO", label: "Tisco Bank", name_th: "ธนาคารทิสโก้", code: "067",},
  { icon: "IBANK.png", value: "IBANK", label: "Islamic Bank of Thailand", name_th: "ธนาคารอิสลามแห่งประเทศไทย", code: "066",},
  { icon: "HSBC.png", value: "HSBC", label: "Hong Kong & Shanghai Corporation", name_th: "ฮ่องกงและเซี่ยงไฮ้แบงกิ้งคอร์ปอเรชั่น", code: "030",},
  { icon: "UOB.png", value: "UOB", label: "United Overseas Bank", name_th: "ธนาคารยูโอบี (ไทย)", code: "024",},
  { icon: "ICBC.png", value: "ICBC", label: "Industrial and Commercial Bank of China Limited", name_th: "ธนาคารพาณิชย์อุตสาหกรรมแห่งประเทศจีน", code: "070",},
  { icon: "BAAC.png", value: "BAAC", label: "Bank for Agriculture and Agricultural Cooperatives", name_th: "ธ.ก.ส.", code: "034",},
  { icon: "GHB.png", value: "GHB", label: "Government Housing Bank", name_th: "ธนาคารอาคารสงเคราะห์", code: "033",},
  { icon: "GSB.png", value: "GSB", label: "Government Savings Bank", name_th: "ธนาคารออมสิน", code: "030",},
  { icon: "BAY.png", value: "BAY", label: "Bank of Ayudhya", name_th: "ธนาคารกรุงศรีอยุธยา", code: "025",},
  { icon: "KTB.png", value: "KTB", label: "Krung Thai Bank", name_th: "ธนาคารกรุงไทย", code: "006",},
  { icon: "BBL.png", value: "BBL", label: "Bangkok Bank", name_th: "ธนาคารกรุงเทพ", code: "002",},
  { icon: "TTB.png", value: "TTB", label: "TTB Thanachart Bank", name_th: "ธนาคารทหารไทยธนชาต", code: "011",},
  { icon: "KBANK.png", value: "KBANK", label: "Kasikorn Bank", name_th: "ธนาคารกสิกรไทย", code: "004",},
  { icon: "SCB.png", value: "SCB", label: "Siam Commercial Bank", name_th: "ธนาคารไทยพาณิชย์", code: "014",},

];